import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { IrCreateTaskModalComponent } from 'components/modal/ir-create-task-modal/ir-create-task-modal.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import {
  IrAddCoResponsibleModalComponent,
  AddCoResponsibleResult
} from 'components/modal/ir-add-co-responsible-modal/ir-add-co-responsible-modal.component';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { formatDate, formatDatetime } from 'util/date.utility';
import type {
  BackofficeInformationRequestCard,
  InformationRequestTaskListItem,
  InformationRequestTaskCard,
  InformationRequestTaskStatus
} from 'types/information-request.interface';
import type { StructuralUnitMinimalListItem } from 'types/structural-unit.interface';

const STATUS_LABELS: Record<InformationRequestTaskStatus, string> = {
  Waiting: 'Gaida',
  Assigned: 'Uzdots',
  Accepted: 'Akceptēts',
  Completed: 'Izpildīts',
  Cancelled: 'Atsaukts'
};

const STATUS_CLASSES: Record<InformationRequestTaskStatus, string> = {
  Waiting: 'status-waiting',
  Assigned: 'status-assigned',
  Accepted: 'status-accepted',
  Completed: 'status-completed',
  Cancelled: 'status-cancelled'
};

@Component({
  selector: 'app-ir-tasks',
  standalone: true,
  imports: [
    TranslateModule,
    NgClass,
    TooltipComponent,
    IrCreateTaskModalComponent,
    ConfirmModalComponent,
    IrAddCoResponsibleModalComponent
  ],
  templateUrl: './ir-tasks.component.html',
  styleUrls: ['./ir-tasks.component.scss']
})
export class IrTasksComponent {
  readonly formatDate = formatDate;
  readonly formatDatetime = formatDatetime;

  @Input() informationRequest!: BackofficeInformationRequestCard;
  @Input() minExecutionOrder: number = 1;
  @Input() isTaskCreationDisabled: boolean = false;
  @Input() structuralUnits: StructuralUnitMinimalListItem[] = [];
  @Input() canEditCoResponsible: boolean = false;

  @Input() tasks: InformationRequestTaskListItem[] = [];
  @Output() taskCreated: EventEmitter<InformationRequestTaskCard> = new EventEmitter();
  @Output() taskStatusChanged: EventEmitter<void> = new EventEmitter();
  @Output() requestUpdated: EventEmitter<BackofficeInformationRequestCard> = new EventEmitter();

  isCreateModalOpen = false;
  actionLoadingTaskId: string | null = null;

  isCompleteConfirmOpen = false;
  isCancelConfirmOpen = false;
  pendingCompleteTask: InformationRequestTaskListItem | null = null;
  pendingCancelTask: InformationRequestTaskListItem | null = null;

  // Co-responsible
  isAddArchiveModalOpen = false;
  isCoResponsibleLoading = false;
  isDeleteCoResponsibleConfirmOpen = false;
  pendingDeleteUnitId: string | null = null;

  constructor(
    private apiService: ApiService,
    private toastService: ToastService
  ) {}

  // --- Co-responsible ---

  get availableStructuralUnits(): StructuralUnitMinimalListItem[] {
    const excludeIds = new Set<string>();
    if (this.informationRequest.responsibleStructuralUnitId) {
      excludeIds.add(this.informationRequest.responsibleStructuralUnitId);
    }
    for (const unit of this.informationRequest.coResponsibleStructuralUnits || []) {
      excludeIds.add(unit.structuralUnitId);
    }
    return this.structuralUnits.filter((u) => !excludeIds.has(u.id));
  }

  openAddArchiveModal(): void {
    this.isAddArchiveModalOpen = true;
  }

  onAddArchiveConfirmed(result: AddCoResponsibleResult): void {
    this.isCoResponsibleLoading = true;
    const irId = this.informationRequest.id;

    if (result.structuralUnitId) {
      this.apiService.addCoResponsibleUnit(irId, {
        structuralUnitId: result.structuralUnitId,
        isConsolidated: result.isConsolidated
      }).subscribe({
        next: (card) => {
          this.isCoResponsibleLoading = false;
          this.isAddArchiveModalOpen = false;
          this.requestUpdated.emit(card);
        },
        error: () => {
          this.toastService.showRequestError();
          this.isCoResponsibleLoading = false;
        }
      });
    } else {
      this.apiService.setConsolidated(irId, {
        isConsolidated: result.isConsolidated
      }).subscribe({
        next: (card) => {
          this.isCoResponsibleLoading = false;
          this.isAddArchiveModalOpen = false;
          this.requestUpdated.emit(card);
        },
        error: () => {
          this.toastService.showRequestError();
          this.isCoResponsibleLoading = false;
        }
      });
    }
  }

  onAddArchiveClosed(): void {
    this.isAddArchiveModalOpen = false;
  }

  removeCoResponsible(structuralUnitId: string): void {
    this.pendingDeleteUnitId = structuralUnitId;
    this.isDeleteCoResponsibleConfirmOpen = true;
  }

  confirmRemoveCoResponsible(): void {
    if (!this.pendingDeleteUnitId) return;
    this.isDeleteCoResponsibleConfirmOpen = false;
    this.isCoResponsibleLoading = true;
    this.apiService.removeCoResponsibleUnit(this.informationRequest.id, this.pendingDeleteUnitId).subscribe({
      next: (card) => {
        this.isCoResponsibleLoading = false;
        this.pendingDeleteUnitId = null;
        this.requestUpdated.emit(card);
      },
      error: () => {
        this.toastService.showRequestError();
        this.isCoResponsibleLoading = false;
        this.pendingDeleteUnitId = null;
      }
    });
  }

  cancelRemoveCoResponsible(): void {
    this.isDeleteCoResponsibleConfirmOpen = false;
    this.pendingDeleteUnitId = null;
  }

  // --- Tasks ---

  openCreateModal(): void {
    this.isCreateModalOpen = true;
  }

  onTaskCreated(task: InformationRequestTaskCard): void {
    this.taskCreated.emit(task);
  }

  acceptTask(task: InformationRequestTaskListItem): void {
    this.actionLoadingTaskId = task.id;
    this.apiService.acceptIrTask(task.id).subscribe({
      next: () => {
        this.actionLoadingTaskId = null;
        this.taskStatusChanged.emit();
      },
      error: () => {
        this.toastService.showRequestError();
        this.actionLoadingTaskId = null;
      }
    });
  }

  completeTask(task: InformationRequestTaskListItem): void {
    this.pendingCompleteTask = task;
    this.isCompleteConfirmOpen = true;
  }

  confirmCompleteTask(): void {
    if (!this.pendingCompleteTask) return;
    this.isCompleteConfirmOpen = false;
    this.actionLoadingTaskId = this.pendingCompleteTask.id;
    this.apiService.completeIrTask(this.pendingCompleteTask.id).subscribe({
      next: () => {
        this.actionLoadingTaskId = null;
        this.pendingCompleteTask = null;
        this.taskStatusChanged.emit();
      },
      error: () => {
        this.toastService.showRequestError();
        this.actionLoadingTaskId = null;
        this.pendingCompleteTask = null;
      }
    });
  }

  cancelCompleteTask(): void {
    this.isCompleteConfirmOpen = false;
    this.pendingCompleteTask = null;
  }

  cancelTask(task: InformationRequestTaskListItem): void {
    this.pendingCancelTask = task;
    this.isCancelConfirmOpen = true;
  }

  confirmCancelTask(): void {
    if (!this.pendingCancelTask) return;
    this.isCancelConfirmOpen = false;
    this.actionLoadingTaskId = this.pendingCancelTask.id;
    this.apiService.cancelIrTask(this.pendingCancelTask.id, {}).subscribe({
      next: () => {
        this.actionLoadingTaskId = null;
        this.pendingCancelTask = null;
        this.taskStatusChanged.emit();
      },
      error: (err) => {
        this.toastService.showValidationOrRequestError(err);
        this.actionLoadingTaskId = null;
        this.pendingCancelTask = null;
      }
    });
  }

  cancelCancelTask(): void {
    this.isCancelConfirmOpen = false;
    this.pendingCancelTask = null;
  }

  getStatusLabel(status: InformationRequestTaskStatus): string {
    return STATUS_LABELS[status] || status;
  }

  getStatusClass(status: InformationRequestTaskStatus): string {
    return STATUS_CLASSES[status] || '';
  }

  getAssigneeDisplay(task: InformationRequestTaskListItem): string {
    if (task.assigneeUserFullName) {
      const codes = task.assigneeUserStructuralUnitCodes;
      if (codes?.length) {
        return `${task.assigneeUserFullName} (${codes.join(', ')})`;
      }
      return task.assigneeUserFullName;
    }
    if (task.assigneeStructuralUnitCode) {
      return task.assigneeStructuralUnitCode;
    }
    if (task.assigneeStructuralUnitName) {
      return task.assigneeStructuralUnitName;
    }
    return '';
  }

  private parseLocalDate(dateStr: string): Date {
    const parts = dateStr.substring(0, 10).split('-');
    return new Date(+parts[0], +parts[1] - 1, +parts[2]);
  }

  isOverdue(task: InformationRequestTaskListItem): boolean {
    if (task.status === 'Completed' || task.status === 'Cancelled') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const execDate = this.parseLocalDate(task.executionDate);
    return execDate < today;
  }

  isApproaching(task: InformationRequestTaskListItem): boolean {
    if (task.status === 'Completed' || task.status === 'Cancelled') return false;
    if (this.isOverdue(task)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const execDate = this.parseLocalDate(task.executionDate);
    return execDate.getTime() === today.getTime();
  }
}
